import { LOCALE_BCP47, type SupportedLocale } from './locales.ts';
import type { LocalizationContext, TimeZone } from './time-zone.ts';

/**
 * The formatting surface — P1-100 step 2.
 *
 * Components NEVER hand-roll a format. Not `date.toLocaleDateString()`, not
 * `` `${amount} ₴` ``, not `n.toFixed(2)`. Every one of those hardcodes a
 * convention: Ukrainian writes `1 234,56 ₴` with a narrow no-break space and the
 * symbol trailing, English writes `£1,234.56`. A component that concatenates has
 * silently chosen one locale's convention for every locale.
 *
 * Two invariants are enforced by the types rather than by review:
 *   1. a formatter cannot be built without a {@link TimeZone} (see time-zone.ts);
 *   2. a formatter cannot be built for an unsupported locale.
 *
 * Every `Intl.*` object is cached. Constructing one parses locale data and is
 * roughly two orders of magnitude more expensive than formatting with it, and a
 * conversation list formats hundreds of timestamps per render.
 */

export type DateInput = Date | number | string;

export type DateStyleName = 'short' | 'medium' | 'long' | 'full';
export type TimeStyleName = 'short' | 'medium';

export interface DateTimeStyle {
  readonly date?: DateStyleName;
  readonly time?: TimeStyleName;
}

export interface NumberStyle {
  readonly minimumFractionDigits?: number;
  readonly maximumFractionDigits?: number;
  /** Thousands grouping. Off for identifiers that happen to be numeric. */
  readonly useGrouping?: boolean;
}

export interface RelativeTimeStyle {
  /** Instant to measure against. Injected so the behaviour is testable without faking the clock. */
  readonly now?: DateInput;
  /** `auto` yields "вчора"/"yesterday"; `always` yields "1 день тому"/"1 day ago". */
  readonly numeric?: 'auto' | 'always';
  readonly style?: 'long' | 'short' | 'narrow';
}

export type ListType = 'conjunction' | 'disjunction';

/**
 * `L` defaults to {@link SupportedLocale}, so every existing use of
 * `Formatters` means exactly what it meant before this became generic.
 *
 * IT IS GENERIC BECAUSE FORMATTING DOES NOT NEED THE VOCABULARY, IT NEEDS A TAG.
 * `locales.ts` keeps `SupportedLocale` narrow on purpose — "is this locale
 * supported?" is answered once, there — and that is the right rule for choosing
 * a CATALOGUE. `Intl` asks a different question: it wants a BCP-47 tag and will
 * resolve any valid one. The widget speaks fifty languages this package has no
 * console catalogue for (`apps/widget/src/i18n/locales.ts` explains why it has
 * its own vocabulary), and it still needs its timestamps formatted correctly.
 * Widening `SupportedLocale` to serve that would have loosened the type that
 * guards catalogue lookup, which is the one place the narrowness earns its keep.
 */
export interface Formatters<L extends string = SupportedLocale> {
  readonly locale: L;
  readonly timeZone: TimeZone;

  /** Calendar date in {@link Formatters.timeZone}. */
  date(value: DateInput, style?: DateStyleName): string;
  /** Wall-clock time in {@link Formatters.timeZone}. */
  time(value: DateInput, style?: TimeStyleName): string;
  dateTime(value: DateInput, style?: DateTimeStyle): string;
  /** ISO-8601 in {@link Formatters.timeZone} — for `<time datetime>` and CSV exports. */
  isoDate(value: DateInput): string;

  number(value: number, style?: NumberStyle): string;
  /** `0.075` renders as `7,5 %` / `7.5%`. Takes a RATIO, not a pre-multiplied percentage. */
  percent(value: number, style?: NumberStyle): string;
  /** Minor units are the caller's problem; this takes a major-unit amount and an ISO-4217 code. */
  currency(value: number, currency: string, style?: NumberStyle): string;
  /** `1234` renders as `1,2 тис.` / `1.2K`. For counters, never for money. */
  compact(value: number): string;

  /**
   * "5 хвилин тому", "вчора". Picks its own unit; below 22 hours the unit is
   * elapsed time (20 minutes ago is "20 хвилин тому" even across midnight), and
   * from a day upwards it is the CALENDAR difference in
   * {@link Formatters.timeZone} — so "вчора" means the previous local date, not
   * "between 24 and 48 hours".
   */
  relativeTime(value: DateInput, style?: RelativeTimeStyle): string;

  /** "Telegram, WhatsApp та Email" — the conjunction is CLDR's, never a literal. */
  list(items: readonly string[], type?: ListType): string;
}

const MILLISECOND = 1;
const SECOND = 1000 * MILLISECOND;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;

/**
 * Bounded because `NumberStyle` is caller-supplied and an unbounded cache keyed
 * by user input is a slow memory leak. The bound is far above the number of
 * distinct formats a UI actually uses; blowing past it means something is
 * generating options per render, and dropping the cache is the cheap answer.
 */
const CACHE_LIMIT = 256;
const intlCache = new Map<string, unknown>();

function cached<T>(key: string, create: () => T): T {
  const hit = intlCache.get(key);
  if (hit !== undefined) return hit as T;
  if (intlCache.size >= CACHE_LIMIT) intlCache.clear();
  const created = create();
  intlCache.set(key, created);
  return created;
}

/** Exported for tests; a cache that cannot be emptied cannot be measured. */
export function clearFormatterCache(): void {
  intlCache.clear();
}

function toDate(value: DateInput): Date {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    // Rendering the string "Invalid Date" into a support agent's inbox is how a
    // bad timestamp reaches production and stays there.
    throw new TypeError(`not a valid date: ${JSON.stringify(value)}`);
  }
  return date;
}

function styleKey(style: NumberStyle): string {
  return `${style.minimumFractionDigits ?? ''}/${style.maximumFractionDigits ?? ''}/${style.useGrouping ?? ''}`;
}

function numberOptions(style: NumberStyle): Intl.NumberFormatOptions {
  // Built by assignment rather than by spreading optionals: with
  // exactOptionalPropertyTypes an explicit `undefined` is not the same as an
  // absent key, and `Intl` treats the two differently for `useGrouping`.
  const options: Intl.NumberFormatOptions = {};
  if (style.minimumFractionDigits !== undefined) {
    options.minimumFractionDigits = style.minimumFractionDigits;
  }
  if (style.maximumFractionDigits !== undefined) {
    options.maximumFractionDigits = style.maximumFractionDigits;
  }
  if (style.useGrouping !== undefined) options.useGrouping = style.useGrouping;
  return options;
}

const ISO_DATE_LOCALE = 'en-CA';

/**
 * The instant's calendar date in `timeZone`, as `YYYY-MM-DD`.
 *
 * `en-CA` is the one widely available locale whose short date format IS ISO-8601,
 * which makes this a supported use of `Intl` rather than string surgery on a
 * localised date.
 */
function isoDateIn(value: Date, timeZone: TimeZone): string {
  const formatter = cached(
    `iso|${timeZone}`,
    () =>
      new Intl.DateTimeFormat(ISO_DATE_LOCALE, {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }),
  );
  return formatter.format(value);
}

/** Days since the epoch in `timeZone` — the unit "yesterday" is actually counted in. */
function calendarDay(value: Date, timeZone: TimeZone): number {
  const [year, month, day] = isoDateIn(value, timeZone).split('-').map(Number);
  return Math.floor(Date.UTC(year ?? 1970, (month ?? 1) - 1, day ?? 1) / (24 * HOUR));
}

/** Months since year 0 in `timeZone`. */
function calendarMonth(value: Date, timeZone: TimeZone): number {
  const [year, month] = isoDateIn(value, timeZone).split('-').map(Number);
  return (year ?? 1970) * 12 + ((month ?? 1) - 1);
}

/**
 * Formatters for a locale this package HAS a catalogue for. Unchanged behaviour:
 * the BCP-47 tag is the one `LOCALE_BCP47` pins, never the runtime's guess.
 */
export function createFormatters(context: LocalizationContext): Formatters {
  return createFormattersForTag(context.locale, LOCALE_BCP47[context.locale], context.timeZone);
}

/**
 * The same formatters for a locale this package does NOT know, given its tag.
 *
 * The caller supplies both because they are two different facts: `locale` is
 * what gets reported back on {@link Formatters.locale} and is the caller's own
 * vocabulary, and `tag` is what `Intl` is constructed with. The widget passes
 * `('zh-Hant', 'zh-Hant-TW', zone)`; keeping them separate is what stops a
 * caller from having to choose between a correct label and a correct calendar.
 *
 * Nothing validates `tag`. `Intl` throws `RangeError` on a malformed one, and a
 * silent repair here would be this package guessing at a locale the caller
 * already declared — which is the behaviour `normalizeLocale` exists to avoid.
 */
export function createFormattersForTag<L extends string>(
  locale: L,
  tag: string,
  timeZone: TimeZone,
): Formatters<L> {
  const dateTimeFormat = (key: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat =>
    cached(
      `dt|${tag}|${timeZone}|${key}`,
      () => new Intl.DateTimeFormat(tag, { ...options, timeZone }),
    );

  const numberFormat = (key: string, options: Intl.NumberFormatOptions): Intl.NumberFormat =>
    cached(`n|${tag}|${key}`, () => new Intl.NumberFormat(tag, options));

  const relativeFormat = (
    numeric: 'auto' | 'always',
    style: 'long' | 'short' | 'narrow',
  ): Intl.RelativeTimeFormat =>
    cached(
      `rt|${tag}|${numeric}|${style}`,
      () => new Intl.RelativeTimeFormat(tag, { numeric, style }),
    );

  return {
    locale,
    timeZone,

    date: (value, style = 'medium') =>
      dateTimeFormat(`d:${style}`, { dateStyle: style }).format(toDate(value)),

    time: (value, style = 'short') =>
      dateTimeFormat(`t:${style}`, { timeStyle: style }).format(toDate(value)),

    dateTime: (value, style = {}) => {
      const dateStyle = style.date ?? 'medium';
      const timeStyle = style.time ?? 'short';
      return dateTimeFormat(`dt:${dateStyle}:${timeStyle}`, { dateStyle, timeStyle }).format(
        toDate(value),
      );
    },

    isoDate: (value) => isoDateIn(toDate(value), timeZone),

    number: (value, style = {}) =>
      numberFormat(`p:${styleKey(style)}`, numberOptions(style)).format(value),

    percent: (value, style = {}) =>
      numberFormat(`%:${styleKey(style)}`, { style: 'percent', ...numberOptions(style) }).format(
        value,
      ),

    currency: (value, currency, style = {}) => {
      if (!/^[A-Z]{3}$/u.test(currency)) {
        throw new RangeError(`not an ISO-4217 currency code: ${JSON.stringify(currency)}`);
      }
      return numberFormat(`c:${currency}:${styleKey(style)}`, {
        style: 'currency',
        currency,
        ...numberOptions(style),
      }).format(value);
    },

    compact: (value) =>
      numberFormat('k', { notation: 'compact', compactDisplay: 'short' }).format(value),

    relativeTime: (value, style = {}) => {
      const target = toDate(value);
      const now = style.now === undefined ? new Date() : toDate(style.now);
      const formatter = relativeFormat(style.numeric ?? 'auto', style.style ?? 'long');

      const deltaMs = target.getTime() - now.getTime();
      const absoluteMs = Math.abs(deltaMs);

      // Sub-day units are pure arithmetic: an hour is an hour in every zone.
      if (absoluteMs < 45 * SECOND) return formatter.format(Math.round(deltaMs / SECOND), 'second');
      if (absoluteMs < 45 * MINUTE) return formatter.format(Math.round(deltaMs / MINUTE), 'minute');
      if (absoluteMs < 22 * HOUR) return formatter.format(Math.round(deltaMs / HOUR), 'hour');

      // Day and above are CALENDAR units, and calendars belong to a zone. A DST
      // change makes one local day 23 hours long, so `delta / 86_400_000` is off
      // by one twice a year, and 23 hours can be either "yesterday" or "2 days
      // ago" depending on where the local midnight fell — which is the exact
      // class of bug this package exists to prevent.
      const days = calendarDay(target, timeZone) - calendarDay(now, timeZone);
      if (Math.abs(days) < 7) return formatter.format(days, 'day');
      if (Math.abs(days) < 28) return formatter.format(Math.trunc(days / 7), 'week');

      const months = calendarMonth(target, timeZone) - calendarMonth(now, timeZone);
      if (Math.abs(months) < 12) return formatter.format(months, 'month');
      return formatter.format(Math.trunc(months / 12), 'year');
    },

    list: (items, type = 'conjunction') =>
      cached(`l|${tag}|${type}`, () => new Intl.ListFormat(tag, { type, style: 'long' })).format(
        items,
      ),
  };
}
